/**
 * Signature helpers — the mobile Pit Link app stores signatures as raw SVG
 * strings (e.g. `<svg xmlns="…"><path d="…"/></svg>`), while our older signature
 * pad in the portal produces PNG data-URLs. This util normalises anything the
 * server hands us into a browser-renderable `<img src>` value, and provides
 * an async rasteriser for PDF embedding.
 */

/** Detect what shape a signature blob is. */
export function detectSigKind(sig) {
  if (!sig || typeof sig !== "string") return "none";
  const s = sig.trim();
  if (!s) return "none";
  if (s.startsWith("data:image/svg+xml")) return "data-svg";
  if (s.startsWith("data:image/")) return "data-image";
  if (s.startsWith("<svg")) return "svg-raw";
  if (/^[A-Za-z0-9+/=]+$/.test(s) && s.length > 200) return "base64";
  return "unknown";
}

/**
 * Convert a signature to a value you can drop directly into `<img src="…">`.
 * Returns `null` if there's nothing renderable.
 */
export function signatureSrc(sig) {
  const kind = detectSigKind(sig);
  if (kind === "none") return null;
  if (kind === "data-svg" || kind === "data-image") return sig;
  if (kind === "svg-raw") {
    // Percent-encode so the SVG survives inline data-URL usage.
    return `data:image/svg+xml;utf8,${encodeURIComponent(sig)}`;
  }
  if (kind === "base64") return `data:image/png;base64,${sig}`;
  return null;
}

/**
 * Rasterise a signature to a PNG data-URL for jsPDF `.addImage`. Async because
 * we need the browser to actually load / render the SVG or image.
 *
 * @param {string} sig               Raw sig blob (svg / data-url / base64).
 * @param {{ width?: number, height?: number, bg?: string }} [opts]
 * @returns {Promise<{ dataUrl: string, width: number, height: number } | null>}
 */
export function rasterizeSignature(sig, opts = {}) {
  return new Promise((resolve) => {
    const src = signatureSrc(sig);
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const targetW = opts.width || 512;
        const targetH = opts.height || Math.round((img.naturalHeight / img.naturalWidth) * targetW) || 256;
        const canvas = document.createElement("canvas");
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext("2d");
        if (opts.bg) {
          ctx.fillStyle = opts.bg;
          ctx.fillRect(0, 0, targetW, targetH);
        }
        ctx.drawImage(img, 0, 0, targetW, targetH);
        const dataUrl = canvas.toDataURL("image/png");
        resolve({ dataUrl, width: targetW, height: targetH });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}
